/* ═══════════════════════════════════════════════════════════════
   IndentHandles — Word's two indent markers, ON THE PARAGRAPH.

   The Studio has always had indent / hanging-indent drag, but only on
   the ruler, only after a level was selected — so in practice nobody
   found it (Josh, 2026-08-22: "those handles don't exist"). Word's own
   answer is two markers on the ruler; ours adds the same two markers
   where the eye already is: on the selected level's sample paragraph.

   - The INDENT marker sits at the number / first-line position and
     moves the whole paragraph (`levels[i].indent`).
   - The HANGING marker sits at the text-body wrap edge and moves only
     the text (`levels[i].hangingIndent`).

   Both write through `indent-edit.ts`, the same module Ruler.tsx now
   uses — one write path, same snapping, same clamps, same undo.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig } from '../../shared/types';
import type { FormaEdit } from './panels/FormaPalettes';
import { measuredScale, offsetWithin } from './spacing-geometry';
import {
  applyIndentEdit,
  findIndentSnap,
  indentSnapTargets,
  nextIndentValue,
  nudgeIndent,
  nudgeStep,
  pxToIn,
  type IndentKind,
} from './indent-edit';

export interface IndentHandlesProps {
  hostRef: React.RefObject<HTMLElement | null>;
  /** The selected level's sample paragraphs, in document order. */
  getBlocks: () => HTMLElement[];
  forma: StandardConfig;
  /** 0-based level index. */
  levelIdx: number;
  /** The level's resolved `padding-left` in px (the text wrap edge). */
  padLeftPx: number;
  /** The level's resolved `text-indent` in px (negative = hanging). */
  textIndentPx: number;
  edit: FormaEdit;
}

interface Metrics {
  /** Top / height of the whole run of this level's paragraphs (the guides). */
  top: number;
  height: number;
  /** The FIRST paragraph's edges — the markers straddle that line, Word-style,
   *  so neither one ever sits on top of the glyphs it controls. */
  firstTop: number;
  firstBottom: number;
  blockLeft: number;
  scale: number;
}

interface DragState {
  kind: IndentKind;
  sx: number;
  sv: number;
  fine: boolean;
}

export function IndentHandles({
  hostRef,
  getBlocks,
  forma,
  levelIdx,
  padLeftPx,
  textIndentPx,
  edit,
}: IndentHandlesProps) {
  const [m, setM] = React.useState<Metrics | null>(null);
  const [drag, setDrag] = React.useState<DragState | null>(null);
  const [liveVal, setLiveVal] = React.useState<number | null>(null);
  const fineTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const scaleRef = React.useRef(1);

  // Measured after every render, for the same reason SpacingBand is: the
  // paragraph box moves whenever ANY level's geometry changes. The equality
  // guard below is what stops the render loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const blocks = getBlocks();
    if (!blocks.length) {
      setM((prev) => (prev === null ? prev : null));
      return;
    }
    const hostRect = host.getBoundingClientRect();
    const scale = measuredScale(hostRect.width, host.offsetWidth);
    scaleRef.current = scale;
    let top = Infinity;
    let bottom = -Infinity;
    let left = Infinity;
    blocks.forEach((el) => {
      const r = el.getBoundingClientRect();
      top = Math.min(top, r.top);
      bottom = Math.max(bottom, r.bottom);
      left = Math.min(left, r.left);
    });
    const firstRect = blocks[0].getBoundingClientRect();
    const next: Metrics = {
      top: (top - hostRect.top) / scale,
      height: Math.max(0, (bottom - top) / scale),
      firstTop: (firstRect.top - hostRect.top) / scale,
      firstBottom: (firstRect.bottom - hostRect.top) / scale,
      blockLeft: offsetWithin(hostRect.left, left, scale),
      scale,
    };
    setM((prev) =>
      prev &&
      Math.abs(prev.top - next.top) < 0.02 &&
      Math.abs(prev.height - next.height) < 0.02 &&
      Math.abs(prev.firstTop - next.firstTop) < 0.02 &&
      Math.abs(prev.blockLeft - next.blockLeft) < 0.02
        ? prev
        : next
    );
  });

  const level = forma.levels?.[levelIdx];
  const snaps = React.useMemo(
    () => indentSnapTargets(forma.levels, levelIdx),
    [forma.levels, levelIdx]
  );

  const beginDrag = (kind: IndentKind) => (e: React.MouseEvent) => {
    if (!level) return;
    e.preventDefault();
    e.stopPropagation();
    const sv = (kind === 'indent' ? level.indent : level.hangingIndent) || 0;
    if (fineTimer.current) clearTimeout(fineTimer.current);
    // Same 1.5s hold → fine mode the ruler has always had.
    fineTimer.current = setTimeout(() => setDrag((d) => (d ? { ...d, fine: true } : d)), 1500);
    setDrag({ kind, sx: e.clientX, sv, fine: false });
  };

  React.useEffect(() => {
    if (!drag || !level) return;
    const baseIndent = level.indent || 0;
    const onMove = (ev: MouseEvent) => {
      const deltaIn = pxToIn((ev.clientX - drag.sx) / (scaleRef.current || 1));
      const final = nextIndentValue({
        kind: drag.kind,
        startValue: drag.sv,
        deltaIn,
        baseIndent,
        fine: drag.fine,
        snaps,
      });
      setLiveVal(final);
      applyIndentEdit(edit, levelIdx, drag.kind, final);
    };
    const onUp = () => {
      setDrag(null);
      setLiveVal(null);
      if (fineTimer.current) clearTimeout(fineTimer.current);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, level, snaps, edit, levelIdx]);

  if (!m || !level) return null;

  const indentIn = level.indent || 0;
  const hangingIn = level.hangingIndent || 0;
  // Measured block box + the level's own resolved CSS: the number sits at
  // `padding-left + text-indent`, the text body at `padding-left`. Those are
  // the same two numbers `bodyParagraphCss` gave the paragraph, so the
  // markers land exactly on the glyphs they control.
  const bodyX = m.blockLeft + padLeftPx;
  const numberX = m.blockLeft + padLeftPx + textIndentPx;
  // Word stacks its two triangles above/below the ruler line; here they
  // straddle the paragraph's own first line — above it for the marker that
  // moves the whole paragraph, below it for the one that moves only the text.
  const yIndent = m.firstTop - 8;
  const yHanging = m.firstBottom + 8;
  const snapped =
    drag && liveVal != null
      ? findIndentSnap(snaps, drag.kind === 'indent' ? liveVal : indentIn + liveVal) != null
      : false;

  const handle = (kind: IndentKind, x: number, y: number, valueIn: number, title: string) => (
    <div
      role="slider"
      tabIndex={0}
      aria-label={`Level ${levelIdx + 1} ${kind === 'indent' ? 'indent' : 'hanging indent'} — ${valueIn.toFixed(2)} inches. Arrow keys adjust.`}
      aria-valuenow={Number(valueIn.toFixed(4))}
      aria-valuemin={0}
      aria-valuetext={`${valueIn.toFixed(2)} inches`}
      title={title}
      className={`sfs-indent-handle sfs-indent-handle-${kind}${drag?.kind === kind ? ' is-active' : ''}`}
      style={{ left: x, top: y }}
      onMouseDown={beginDrag(kind)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        e.stopPropagation();
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        nudgeIndent(edit, levelIdx, kind, dir * nudgeStep(e.shiftKey));
      }}
    />
  );

  return (
    <>
      <div
        className="sfs-indent-guide"
        style={{ left: numberX, top: m.top, height: m.height }}
        aria-hidden="true"
      />
      <div
        className={`sfs-indent-guide sfs-indent-guide-body${snapped ? ' is-snapped' : ''}`}
        style={{ left: bodyX, top: m.top, height: m.height }}
        aria-hidden="true"
      />
      {handle(
        'indent',
        numberX,
        yIndent,
        indentIn,
        `Indent · ${indentIn.toFixed(2)}″ — drag to move the whole paragraph`
      )}
      {handle(
        'hanging',
        bodyX,
        yHanging,
        hangingIn,
        `Hanging · ${hangingIn.toFixed(2)}″ — drag to move the text off the number`
      )}
      {drag && liveVal != null && (
        <span
          className="sfs-indent-readout"
          style={{
            left: drag.kind === 'indent' ? numberX : bodyX,
            top: (drag.kind === 'indent' ? yIndent : yHanging) - 24,
          }}
        >
          {liveVal.toFixed(2)}″ {drag.kind === 'indent' ? 'indent' : 'hanging'}
        </span>
      )}
    </>
  );
}
